import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import type { Task } from 'ember-concurrency';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and } from 'ember-truth-helpers';
import { sourceToAbilityMap } from 'garaje/abilities/entry-approval';
import type { GetCanReviewOrReadBlocklistMatchModel } from 'garaje/abilities/security';
import Blocklist from 'garaje/components/approvals/screeners/blocklist';
import IdScanning from 'garaje/components/approvals/screeners/id-scanning';
import ReviewButtons from 'garaje/components/approvals/screeners/review-buttons';
import VisualCompliance from 'garaje/components/approvals/screeners/visual-compliance';
import UnifiedScreenersModal from 'garaje/components/approvals/unified-screeners-modal';
import TruncateWithTooltip from 'garaje/components/truncate-with-tooltip';
import Card from 'garaje/components/ui/card';
import MessageBox from 'garaje/components/ui/classic/message-box';
import UiButton from 'garaje/components/ui-button';
import type DocumentUploadPageModel from 'garaje/models/document-upload-page';
import type FlowModel from 'garaje/models/flow';
import type ScreenerResult from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import getModelClassName from 'garaje/utils/get-model-class-name';
import { getScreenerDisplayName } from 'garaje/utils/get-screener-display-name';
import type IdNameReview from 'garaje/utils/id-name-review';
import { allRequiredDocumentsUploaded } from 'garaje/utils/required-docs-status';
import { reads } from 'macro-decorators';

interface ScreenerWrapperSignature {
  Args: {
    model: GuestWithVisitApproval;
    result: ScreenerResult;
    approveTask: Task<void, []>;
    denyTask: Task<void, []>;
    showDenyButton?: boolean;
    nameReview: IdNameReview;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}
interface ScreenerSignature {
  Blocks: {
    default: [];
  };
}

function getScreenerComponent(model: GuestWithVisitApproval, result: ScreenerResult, nameReview: IdNameReview) {
  if (result.isBlocklist()) {
    return <template>
      <Blocklist @model={{model}} @result={{result}}>{{yield}}</Blocklist>
    </template> satisfies TOC<ScreenerSignature>;
  } else if (result.isVisualCompliance()) {
    return <template>
      <VisualCompliance @model={{model}} @result={{result}}>{{yield}}</VisualCompliance>
    </template> satisfies TOC<ScreenerSignature>;
  } else if (result.isIdScanning()) {
    return <template>
      <IdScanning @model={{model}} @result={{result}} @nameReview={{nameReview}}>{{yield}}</IdScanning>
    </template> satisfies TOC<ScreenerSignature>;
  } else if (result.isPreRegistrationRequired()) {
    return <template>
      <li class="text-sm mt-2">Visitor is not invited.</li>
      {{yield}}
    </template> satisfies TOC<ScreenerSignature>;
  } else if (result.isCapacityScreening()) {
    const hasCapacityNumbers =
      result.report['expected-capacity-count'] !== undefined && result.report['capacity-limit'] !== undefined;
    const capacityCopy = hasCapacityNumbers
      ? `Capacity was at ${result.report['expected-capacity-count']} of ${result.report['capacity-limit']} when checked.`
      : 'Location is at capacity.';

    return <template>
      <li class="text-sm mt-2">{{capacityCopy}}</li>
      {{yield}}
    </template> satisfies TOC<ScreenerSignature>;
  } else if (result.isVisitorDocuments()) {
    // Derive upload-completeness from the flow's document-upload-page configs relation.
    // Fall back to "all uploaded" copy when the relation is absent (legacy / no custom docs).
    const flow = model.belongsTo('flow').value() as FlowModel | null;
    const page = flow?.belongsTo('documentUploadPage').value() as DocumentUploadPageModel | null;
    const activeConfigs = page?.enabled ? page.userDocumentTemplateConfigurations.slice().filter((c) => c.active) : [];
    const visitorDocuments = model.visitorDocuments?.slice() ?? [];
    const allUploaded = allRequiredDocumentsUploaded(activeConfigs, visitorDocuments);
    const visitorDocumentsCopy = allUploaded
      ? 'Please review the documents this visitor uploaded.'
      : 'Visitor did not upload all required documents, please review.';

    return <template>
      <li class="text-sm mt-2">{{visitorDocumentsCopy}}</li>
      {{yield}}
    </template> satisfies TOC<ScreenerSignature>;
  } else if (result.isTempScreening()) {
    return <template>
      <li class="text-sm mt-2">{{result.tempCheckFailureMessage}}</li>
      {{yield}}
    </template> satisfies TOC<ScreenerSignature>;
  } else if (result.isIdentityVerification()) {
    return <template>
      <li class="text-sm mt-2">{{result.idVerificationFailureMessage}}</li>
      {{yield}}
    </template> satisfies TOC<ScreenerSignature>;
  } else if (result.isOutgoingInvite()) {
    return <template>
      <li class="text-sm mt-2">Outgoing invite requires approval.</li>
      {{yield}}
    </template> satisfies TOC<ScreenerSignature>;
  } else if (result.isConditionalRule() && result.report.reason) {
    return <template>
      <li class="text-sm mt-2" data-test-conditional-rule-reason>
        <TruncateWithTooltip @value={{result.report.reason}} @length={{50}} @side="bottom" />
      </li>
      {{yield}}
    </template> satisfies TOC<ScreenerSignature>;
  }

  return <template>
    <li class="text-sm mt-2">Approval required.</li>
    {{yield}}
  </template> satisfies TOC<ScreenerSignature>;
}

export default class ScreenerWrapper extends Component<ScreenerWrapperSignature> {
  @service declare abilities: AbilitiesService;
  @service declare state: StateService;
  @service declare metrics: MetricsService;

  @reads('state.features.canAccessMultiPartApprovals') canAccessMultiPartApprovals!: boolean;

  @tracked showUnifiedScreenersModal = false;

  get didNotContactVisualCompliance(): boolean {
    return this.args.result.isVisualCompliance() && this.args.result.report['call-status'] === 'unreachable';
  }

  get canOpenUnifiedScreenersModal(): boolean {
    const { result } = this.args;
    if (!result.details) return false;

    const source = sourceToAbilityMap[result.screenerSource] || result.screenerSource;
    const canReview = this.abilities.can(`review ${source}`, this.#permissionsModel);
    const canView = this.abilities.can(`view ${source}`, this.#permissionsModel);

    return canReview || canView;
  }

  get canReview(): boolean {
    const { result, showDenyButton } = this.args;

    let source: string | undefined;

    if (this.canAccessMultiPartApprovals) {
      // only need to check one screener
      source = sourceToAbilityMap[result.screenerSource] || result.screenerSource;
    } else {
      // checks all screeners in review state
      source = 'all reviewable entry-approvals';
    }

    const canReview = this.abilities.can(`review ${source}`, this.#permissionsModel);

    // if the deny option is provided, there is only a single review action available;
    if (showDenyButton) return canReview;

    // if there is more than one review action, check for multi part feature
    return canReview && this.canAccessMultiPartApprovals;
  }

  get #permissionsModel(): GetCanReviewOrReadBlocklistMatchModel {
    return {
      context: 'location',
      parent: this.args.model,
      visitApproval: this.args.model.visitApproval,
    };
  }

  @action
  toggleUnifiedScreenersModal(): void {
    this.showUnifiedScreenersModal = !this.showUnifiedScreenersModal;

    if (this.showUnifiedScreenersModal) {
      this.metrics.trackEvent('Unified Screener Modal Opened', {
        screenerSource: this.args.result.screenerSource,
        from: `Drilled in view review section: ${getModelClassName(this.args.model)}`,
      });
    }
  }

  @action
  didApprove(result: ScreenerResult, didApprove: () => void): void {
    this.metrics.trackEvent('Unified Screener Modal Approve Clicked', {
      screenerSource: result?.screenerSource,
      from: `Drilled in view review section: ${getModelClassName(this.args.model)}`,
    });

    didApprove();
  }

  @action
  didDeny(result: ScreenerResult): void {
    this.metrics.trackEvent('Unified Screener Modal Deny Clicked', {
      screenerSource: result?.screenerSource,
      from: `Drilled in view review section: ${getModelClassName(this.args.model)}`,
    });
  }

  <template>
    {{#if this.didNotContactVisualCompliance}}
      <MessageBox
        @type="warning"
        @header="⚠️ Visual Compliance unavailable"
        class="mb-4"
        data-test-visual-compliance-unreachable-warning
      >
        <p class="mb-0">
          Error sending details of this visitor to Visual Compliance
        </p>
      </MessageBox>
    {{/if}}

    <Card class="border-carbon-20 mb-8" data-test-card-for={{@result.screenerSource}}>
      <h5 class="text-sm font-bold mb-1">{{getScreenerDisplayName @result.screenerSource}}</h5>

      <ul class="list-none p-0 m-0 leading-normal" data-test-approval-review-reports>
        {{#let (getScreenerComponent @model @result @nameReview) as |ScreenerComponent|}}
          {{#if ScreenerComponent}}
            <ScreenerComponent>
              {{#if this.canOpenUnifiedScreenersModal}}
                <UiButton
                  class="btn btn--small btn--outline w-full mt-2"
                  data-test-review-button={{@result.screenerSource}}
                  {{on "click" this.toggleUnifiedScreenersModal}}
                >
                  {{svgJar "open-eye" height="12" width="12" class="flex-none mr-2 align-middle"}}
                  Review
                </UiButton>
              {{/if}}
              {{#if this.canReview}}
                <ReviewButtons
                  @approveTask={{@approveTask}}
                  @denyTask={{if @showDenyButton @denyTask}}
                  @result={{@result}}
                  @model={{@model}}
                  @disabled={{@nameReview.isReviewing}}
                />
              {{/if}}
            </ScreenerComponent>
          {{/if}}
        {{/let}}
      </ul>
    </Card>

    {{#if this.showUnifiedScreenersModal}}
      <UnifiedScreenersModal @model={{@model}} @focusedResult={{@result}} @onClose={{this.toggleUnifiedScreenersModal}}>
        <:actions as |result onApprove|>
          {{#if (and this.canReview result.isReview)}}
            {{! always render deny for modal }}
            <ReviewButtons
              @onApprove={{fn this.didApprove result onApprove}}
              @onDeny={{fn this.didDeny result}}
              @approveTask={{@approveTask}}
              @denyTask={{@denyTask}}
              @result={{result}}
              @model={{@model}}
              @disabled={{@nameReview.isReviewing}}
            >
              <:deny>
                Deny visit
              </:deny>
            </ReviewButtons>
          {{/if}}
        </:actions>
      </UnifiedScreenersModal>
    {{/if}}
  </template>
}
